import { type DecisionGraph, getNodeResult } from "../../../../utils";

/** A node by its path or guid. */
export const findGraphNode = (graph: DecisionGraph | null | undefined, id: string): any => {
  if (!graph) return null;
  const found = graph.ref(id);
  return found === undefined ? null : (graph.node(found) ?? null);
};

/** What an entity instance is called: its identifier attribute's value, or its index. */
export const instanceIdentifier = (graph: DecisionGraph, instancePath: string): unknown => {
  const id = graph.identifierNode(instancePath);
  const value = id === undefined ? undefined : getNodeResult(graph.node(id) as any);
  return value !== undefined ? value : instancePath.split("/").pop();
};

/**
 * Every node's key with the fields search reads (description, validation, hidden, path, id): read from the graph's
 * rule data per id, without making node objects.
 */
export function* searchableNodes(
  graph: DecisionGraph,
  visible?: Uint8Array | null,
): Generator<[string, { description?: string; validation?: any; hidden?: boolean; path?: string; id?: string }]> {
  for (const id of graph.ids()) {
    if (visible && !visible[id]) continue;
    const key = graph.deref(id);
    yield [
      key,
      {
        description: graph.field(id, "description") as string | undefined,
        validation: graph.field(id, "validation"),
        hidden: graph.field(id, "hidden") as boolean | undefined,
        path: graph.isGlobal(id) ? undefined : key,
        id: graph.guid(id),
      },
    ];
  }
}

/** Selected attribute IDs, without materializing node objects. An empty selection means every listed node. */
export function* decisionGraphAttributeIds(graph: DecisionGraph, attributeIds: string[]): Generator<number> {
  if (attributeIds.length === 0) {
    for (const id of graph.ids()) yield id;
    return;
  }
  const seen = new Set<number>();
  for (const attributeId of attributeIds) {
    const guid = attributeId.split("/").pop() ?? attributeId;
    const ids = Array.from(graph.instancesOf(guid));
    const global = graph.ref(guid);
    if (global !== undefined && graph.isListed(global)) ids.unshift(global);
    for (const id of ids) {
      const path = graph.deref(id);
      if (attributeId.includes("/") && path !== attributeId && !path.endsWith(`/${attributeId}`)) continue;
      if (!seen.has(id)) { seen.add(id); yield id; }
    }
  }
}

/**
 * The nodes for attribute ids (guids, or paths / path suffixes ending in one), keyed by path: from each attribute's
 * own nodes (its global copy and instances), not a scan of every node.
 */
export const decisionGraphAttributeNodes = (graph: DecisionGraph, attributeIds: string[]): Map<string, any> => {
  const found = new Map<string, any>();
  if (attributeIds.length === 0) return found;
  for (const id of decisionGraphAttributeIds(graph, attributeIds)) {
    const node = graph.node(id);
    if (node) found.set(graph.deref(id), node);
  }
  return found;
};
